import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChangePassword from './ChangePassword';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const AdminLayout = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { t } = useTranslation();

  // Navigation items for admin users
  const adminNavItems = [
    { path: '/admin', label: t('admin.dashboard'), icon: '📊' },
    { path: '/admin/season', label: t('admin.seasonManagement.title'), icon: '🏆' },
    { path: '/admin/games', label: t('admin.gamesManagement'), icon: '🏀' },
    { path: '/admin/playoffs', label: t('admin.playoffManagement'), icon: '🏅' },
    { path: '/admin/players', label: t('admin.playerManagement'), icon: '👥' },
    { path: '/admin/roster', label: t('admin.rosterManagement'), icon: '📋' },
    { path: '/admin/trades', label: t('admin.tradeManagement'), icon: '🔄' },
    { path: '/admin/teams', label: t('admin.teamManagement'), icon: '🏟️' },
    { path: '/admin/invite-codes', label: t('admin.inviteCodes'), icon: '🎫' },
    { path: '/admin/users', label: t('admin.userManagement'), icon: '👤' },
    { path: '/admin/documentation', label: t('admin.documentation'), icon: '📚' },
    { path: '/settings', label: t('admin.settings'), icon: '⚙️' }
  ];

  // Navigation items for team owners
  const ownerNavItems = [
    { path: '/admin', label: t('admin.dashboard'), icon: '📊' },
    { path: '/admin/games', label: t('admin.myGames'), icon: '🏀' },
    { path: '/admin/players', label: t('admin.playerManagement'), icon: '👥' },
    { path: '/admin/roster', label: t('admin.rosterManagement'), icon: '📋' },
    { path: '/admin/trades', label: t('admin.myTrades'), icon: '🔄' },
    { path: '/admin/gm-documentation', label: t('admin.gmDocumentation'), icon: '📖' },
    { path: '/settings', label: t('admin.settings'), icon: '⚙️' }
  ];

  const navItems = isAdmin() ? adminNavItems : ownerNavItems;

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex">
      {/* Sidebar */}
      <div
        className={`bg-gray-900 text-white transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        <div className="p-4">
          {/* Logo/Title */}
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && (
              <h2 className="text-xl font-bold">{t('admin.panel')}</h2>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-400 hover:text-white"
            >
              {sidebarOpen ? '◀' : '▶'}
            </button>
          </div>

          {/* User Info */}
          <div className="mb-6 pb-4 border-b border-gray-700">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                {user.username.charAt(0).toUpperCase()}
              </div>
              {sidebarOpen && (
                <div className="ml-3">
                  <p className="font-semibold">{user.username}</p>
                  <p className="text-xs text-gray-400 capitalize">{user.role}</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                {sidebarOpen && <span className="ml-3">{item.label}</span>}
              </Link>
            ))}
          </nav>

          {/* Logout Button */}
          <div className="mt-8 space-y-2">
            <button
              onClick={() => setShowChangePassword(true)}
              className="flex items-center px-4 py-3 rounded-lg text-gray-300 hover:bg-blue-600 hover:text-white transition-colors w-full"
            >
              <span className="text-xl">🔒</span>
              {sidebarOpen && <span className="ml-3">{t('admin.changePassword')}</span>}
            </button>
            
            <button
              onClick={logout}
              className="flex items-center px-4 py-3 rounded-lg text-gray-300 hover:bg-red-600 hover:text-white transition-colors w-full"
            >
              <span className="text-xl">🚪</span>
              {sidebarOpen && <span className="ml-3">{t('admin.logout')}</span>}
            </button>
          </div>

          {/* Back to Website */}
          <div className="mt-2">
            <Link
              to="/"
              className="flex items-center px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors w-full"
            >
              <span className="text-xl">🏠</span>
              {sidebarOpen && <span className="ml-3">{t('admin.backToWebsite')}</span>}
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="bg-white dark:bg-gray-800 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                {isAdmin() ? t('admin.superAdminDashboard') : t('admin.teamOwnerDashboard')}
              </h1>
              {user.team && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.managingTeam', { teamName: user.team.name })}
                </div>
              )}
            </div>
            
            {/* Language Switcher */}
            <div className="flex items-center">
              <LanguageSwitcher variant="admin" />
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 p-6 overflow-auto dark:bg-gray-900">
          <Outlet />
        </div>
      </div>
      
      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default AdminLayout;
